<template>
  <div class="websocket-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>WebSocket 测试控制台</span>
        <el-tag :type="statusTagType" style="float: right">{{ statusText }}</el-tag>
      </div>

      <el-form label-width="100px">
        <el-form-item label="服务地址">
          <el-input v-model="url" placeholder="ws://localhost:8080/ws">
             <el-button slot="append" @click="toggleConnection" :type="isConnected ? 'danger' : 'primary'">
               {{ isConnected ? '断开连接' : '点击连接' }}
             </el-button>
          </el-input>
          <div class="tips">提示：可以使用 wss://echo.websocket.org 进行测试</div>
        </el-form-item>
        
        <el-form-item label="消息内容">
          <el-input v-model="message" placeholder="请输入要发送的消息" @keyup.enter.native="sendMessage">
            <el-button slot="append" @click="sendMessage" :disabled="!isConnected">发送</el-button>
          </el-input>
        </el-form-item>
      </el-form>

      <div class="log-title">通信日志：</div>
      <div class="log-container">
        <div v-if="logs.length === 0" class="empty-log">暂无日志</div>
        <div v-for="(log, index) in logs" :key="index" class="log-item">
          <span class="log-time">[{{ log.time }}]</span>
          <span :class="['log-type', log.type]">{{ log.typeText }}:</span>
          <span class="log-content">{{ log.content }}</span>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'WebSocketDemo',
  data() {
    return {
      // WebSocket 服务地址，这里默认使用一个公共的 Echo 服务（发送什么返回什么）
      url: 'wss://echo.websocket.org', 
      message: '',
      socket: null,
      isConnected: false,
      logs: []
    }
  },
  computed: {
    statusText() {
      return this.isConnected ? '已连接' : '未连接';
    },
    statusTagType() {
      return this.isConnected ? 'success' : 'info';
    }
  },
  destroyed() {
    // 组件销毁时断开连接，防止内存泄漏
    // 这一点非常重要，否则切换页面后连接可能还在后台运行
    this.disconnect();
  },
  methods: {
    // 切换连接状态
    toggleConnection() {
      if (this.isConnected) {
        this.disconnect();
      } else {
        this.initWebSocket();
      }
    },
    
    // 初始化 WebSocket (核心方法)
    initWebSocket() {
      if (!this.url) {
        this.$toast.error('请输入WebSocket地址');
        return;
      }
      
      // 1. 检查浏览器是否支持 WebSocket
      if (typeof(WebSocket) === "undefined") {
        this.$toast.error("您的浏览器不支持WebSocket");
        return;
      }

      try {
        // 2. 实例化 WebSocket 对象
        // new WebSocket(url) 会自动开始尝试连接
        this.socket = new WebSocket(this.url);
        this.addLog('系统', '正在连接...');
        
        // 3. 绑定四个主要事件监听器
        
        // 当连接成功建立时触发
        this.socket.onopen = this.onOpen;
        
        // 当收到服务器发送的消息时触发
        this.socket.onmessage = this.onMessage;
        
        // 当连接发生错误时触发
        this.socket.onerror = this.onError;
        
        // 当连接关闭时触发
        this.socket.onclose = this.onClose;

      } catch (error) {
        this.addLog('错误', 'WebSocket实例化失败');
        console.error(error);
      }
    },
    
    // 连接成功的回调
    onOpen() {
      this.isConnected = true;
      this.addLog('系统', '连接成功');
      this.$toast.success('WebSocket连接成功');
      // 连接成功后，可以发送心跳包或者鉴权信息，视具体业务需求而定
    },

    // 收到消息的回调
    onMessage(event) {
      // event.data 是服务器返回的实际数据
      console.log('收到消息:', event.data);
      this.addLog('收到', event.data);
      
      // 注意：如果服务器返回的是 JSON 字符串，通常需要 JSON.parse(event.data) 解析
    },

    // 连接错误的回调
    onError() {
      this.isConnected = false;
      this.addLog('错误', '连接发生错误');
      this.$toast.error('WebSocket连接发生错误');
    },

    // 连接关闭的回调
    onClose() {
      this.isConnected = false;
      this.addLog('系统', '连接已关闭');
    },

    // 发送消息
    sendMessage() {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        this.$toast.warning('请先连接WebSocket');
        return;
      }
      if (!this.message) {
        this.$toast.warning('请输入消息内容');
        return;
      }
      
      // 使用 socket.send() 发送数据
      this.socket.send(this.message);
      this.addLog('发送', this.message);
      this.message = ''; // 清空输入框
    },

    // 断开连接
    disconnect() {
      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }
    },

    // 辅助方法：添加日志到界面显示
    addLog(type, content) {
      const now = new Date();
      const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
      let typeText = type;
      if (type === 'received') typeText = '收到';
      if (type === 'sent') typeText = '发送';
      
      this.logs.unshift({
        time,
        type,
        typeText,
        content
      });
    }
  }
}
</script>

<style scoped>
.websocket-container {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
}
.box-card {
  max-width: 800px;
  margin: 0 auto;
}
.tips {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 5px;
}
.log-title {
  margin: 20px 0 10px;
  font-weight: bold;
}
.log-container {
  border: 1px solid rgba(31, 45, 61, 0.06);
  padding: 10px;
  height: 400px;
  overflow-y: auto;
  background-color: var(--surface-1);
  border-radius: 4px;
}
.empty-log {
  text-align: center;
  color: #C0C4CC;
  margin-top: 150px;
}
.log-item {
  margin-bottom: 8px;
  font-size: 14px;
  border-bottom: 1px dashed #EBEEF5;
  padding-bottom: 5px;
  word-break: break-all;
}
.log-time {
  color: #909399;
  margin-right: 10px;
  font-family: monospace;
}
.log-type {
  font-weight: bold;
  margin-right: 10px;
  display: inline-block;
  width: 40px;
}
.log-type.系统 { color: #409EFF; }
.log-type.收到 { color: #67C23A; }
.log-type.发送 { color: #E6A23C; }
.log-type.错误 { color: #F56C6C; }
</style>
